<script setup>
import { computed } from 'vue'
import { formatMoney, intervalName } from '@/helpers/billing-format'
import { featureLabel, macsLabel } from '@/helpers/billing-plans'

/**
 * One plan on the plan page: its price at the length being looked at, what it
 * includes, and the one thing the owner can do about it (what `choice` says).
 * It doesn't decide what choosing does; the page does.
 */
const props = defineProps({
    // `{ key, free, name, entitlements, price }`; `price` is null for a plan not
    // sold at the length being looked at.
    option: {
        type: Object,
        required: true,
    },
    // What planChoice() worked out for this plan.
    choice: {
        type: Object,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['choose'])

const features = computed(() =>
    Object.entries(props.option.entitlements?.features ?? {}).map(
        ([key, included]) => ({ key, label: featureLabel(key), included })
    )
)

const price = computed(() => props.option.price)
const isAnnual = computed(() => price.value?.interval === 'year')

// What an annual plan comes to a month, to set beside the monthly plans.
const perMonth = computed(() =>
    isAnnual.value
        ? formatMoney(Math.round(price.value.amount / 12), price.value.currency)
        : null
)

const current = computed(() => props.choice.state === 'current')
const canChoose = computed(
    () => props.choice.state === 'choose' && !props.disabled
)
</script>

<template>
    <article
        :data-testid="`plan-option-${option.key}`"
        :data-state="choice.state"
        class="flex h-full flex-col rounded-xl border bg-white p-6 shadow-sm"
        :class="
            current ? 'border-sky-500 ring-1 ring-sky-500' : 'border-taupe-200'
        "
    >
        <div class="flex items-start justify-between gap-3">
            <h3 class="text-lg font-semibold text-taupe-900">
                {{ option.name }}
            </h3>
            <span
                v-if="choice.badge"
                data-testid="plan-option-badge"
                class="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-800"
                >{{ choice.badge }}</span
            >
        </div>

        <p class="mt-3" data-testid="plan-option-price">
            <template v-if="option.free">
                <span class="text-3xl font-semibold text-taupe-900">Free</span>
            </template>
            <template v-else-if="price">
                <span class="text-3xl font-semibold text-taupe-900">{{
                    formatMoney(price.amount, price.currency)
                }}</span>
                <span class="text-sm text-taupe-600">
                    / {{ isAnnual ? 'year' : 'month' }}</span
                >
            </template>
            <template v-else>
                <span class="text-sm text-taupe-600"
                    >Not sold with this billing length.</span
                >
            </template>
        </p>
        <p
            v-if="perMonth"
            class="text-xs text-taupe-500"
            data-testid="plan-option-per-month"
        >
            {{ perMonth }} a month, billed {{ intervalName(price.interval) }}
        </p>

        <p
            class="mt-5 text-sm font-medium text-taupe-800"
            data-testid="plan-option-macs"
        >
            {{ macsLabel(option.entitlements?.macs?.limit ?? null) }}
        </p>

        <ul class="mt-3 space-y-2 text-sm" data-testid="plan-option-features">
            <li
                v-for="feature in features"
                :key="feature.key"
                :data-feature="feature.key"
                :data-included="feature.included"
                class="flex items-center gap-2"
                :class="feature.included ? 'text-taupe-800' : 'text-taupe-400'"
            >
                <svg
                    v-if="feature.included"
                    class="h-4 w-4 flex-none text-emerald-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fill-rule="evenodd"
                        d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z"
                        clip-rule="evenodd"
                    />
                </svg>
                <svg
                    v-else
                    class="h-4 w-4 flex-none"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        d="M5.3 5.3a1 1 0 0 1 1.4 0L10 8.6l3.3-3.3a1 1 0 1 1 1.4 1.4L11.4 10l3.3 3.3a1 1 0 0 1-1.4 1.4L10 11.4l-3.3 3.3a1 1 0 0 1-1.4-1.4L8.6 10 5.3 6.7a1 1 0 0 1 0-1.4Z"
                    />
                </svg>
                <span
                    >{{ feature.label
                    }}<span class="sr-only">{{
                        feature.included ? ' (included)' : ' (not included)'
                    }}</span></span
                >
            </li>
        </ul>

        <div class="mt-auto pt-6">
            <zen-button
                :variant="
                    canChoose && choice.primary !== false
                        ? 'primary'
                        : 'secondary'
                "
                full-width
                :disabled="!canChoose"
                :data-testid="`plan-choose-${option.key}`"
                @click="emit('choose')"
            >
                {{ choice.label }}
            </zen-button>
        </div>
    </article>
</template>
